import { StateParams } from '@uirouter/core';
import * as angular from 'angular';
import * as _ from 'underscore';
import { IImgFileModel } from '../../../../../app.common/modules/common/directives/models';
import { IPagination } from '../../../../../app.common/modules/common/models';
import { PopupServiceCancel } from '../../../../../app.common/modules/common/models/popupServiceCancel';
import { BlobService } from '../../../../../app.common/modules/common/services/blob.service';
import { NotificationService } from '../../../../../app.common/modules/common/services/notification.service';
import { PopupService } from '../../../../../app.common/modules/common/services/popup.service';
import { IUser } from '../../../account/model';
import { IPaginationStoresFilter } from '../../../api';
import { RetailerService } from '../../../api/retailer.service';
import { StoreListItem } from '../../../core';
import { IRetailerRootScope } from '../../../core/models';
import { IShopBusinessType } from '../../models';
import { ShopService } from '../../shop.service';

interface IImageSize {
    thumb: {
        w: number; h: number;
    };
    w: number;
    h: number;
    file: number;
}

interface IStoreListItem extends StoreListItem {
    logoUrl: string;
    logoID: number;
    coverUrl: string;
    coverID: number;
    address: string;
    businessType: IShopBusinessType;
}

export class ShopsController {
    public static $inject = ['$rootScope', '$scope', '$q', '$stateParams', '$timeout', 'retailerService', 'shopService', 'notificationService', 'popupService', 'blobService', '$translate', '$uibModal'];

    public showActiveShop: boolean = decodeURIComponent(this.$stateParams.active).toLowerCase() !== 'false';
    public waiting: boolean = false;
    public storeName: string = '';
    public file: { cover: File & IImgFileModel, logo: File & IImgFileModel } = {
        cover: null,
        logo: null
    };

    public size: { cover: IImageSize, logo: IImageSize } = {
        cover: {
            thumb: { w: 320, h: 160 },
            w: 1200,
            h: 600,
            file: 10
        },
        logo: {
            thumb: { w: 66, h: 66 },
            w: 210,
            h: 210,
            file: 5
        }
    };

    public settings: { active: IPagination, archived: IPagination, getPagingSettings: (active: boolean, storeName: string) => IPaginationStoresFilter } = {
        active: {
            total: 0,
            pageSize: 8,
            page: 1
        },
        archived: {
            total: 0,
            pageSize: 8,
            page: 1
        },
        getPagingSettings: (_active: boolean, _storeName: string): IPaginationStoresFilter => {
            return {
                limit: _active ? this.settings.active.pageSize : this.settings.archived.pageSize,
                offset: _active ? ((this.settings.active.page - 1) * this.settings.active.pageSize) : ((this.settings.archived.page - 1) * this.settings.archived.pageSize),
                active: _active,
                sortColumn: '',
                sortOrder: '',
                storeName: _storeName
            };
        }
    };

    public tooltip: {} = {
        websiteUrl: this.$translate.instant('add_website_url'),
        description: this.$translate.instant('enter_place_description'),
        openingHours: this.$translate.instant('opening_hours_tooltip'),
        inventoryType: this.$translate.instant('inventory_type_tooltip'),
        campaigns: this.$translate.instant('campaigns_tooltip'),
        cover: 'Min ' + this.size.cover.w + '×' + this.size.cover.w + 'px, max ' + this.size.cover.file + 'MB',
        logo: 'Min ' + this.size.logo.w + '×' + this.size.logo.w + 'px, max ' + this.size.logo.file + 'MB',
    };
    public shops: IStoreListItem[];
    public archivedShops: IStoreListItem[];
    public isNfrnRetailer: boolean;

    private uploadedCover: () => void;
    private businessTypes: IShopBusinessType[] = [];
    private isOnLogoEdit: boolean = false;

    constructor(
        private $rootScope: IRetailerRootScope,
        private $scope: ng.IScope,
        private $q: ng.IQService,
        private $stateParams: StateParams,
        private $timeout: ng.ITimeoutService,
        private retailerService: RetailerService,
        private shopService: ShopService,
        private notificationService: NotificationService,
        private popupService: PopupService,
        private blobService: BlobService,
        private $translate: angular.translate.ITranslateService,
        public $uibModal: ng.ui.bootstrap.IModalService,
    ) {
        const unwatch = $rootScope.$watch('userData', async (userData: IUser) => {
            if (userData.permission) {
                unwatch();
                this.isNfrnRetailer = userData.permission === this.$rootScope.enums.RetailerPermission.Nfrn;
            }
        }, true);
    }

    public uploadCover = (shop) => {
        const el = angular.element('#coverStore');
        this.$timeout(() => {
            this.uploadedCover = function() { this._uploadedCover(shop); };
            el.click();
        });
    }

    public uploadLogo = async (store: IStoreListItem) => {
        if (this.isOnLogoEdit) {
            return;
        }
        const minSize = {
            w: this.size.logo.w,
            h: this.size.logo.h
        };

        try {
            this.isOnLogoEdit = true;
            const image = await this.$uibModal.open({
                component: 'jispPayAvatar',
                resolve: {
                    avatarId: () => store.LogoID,
                    title: () => this.$translate.instant('store_photo'),
                    sizeText: () =>
                        this.$translate.instant('store_photo_min_size_hint', {width: 210, height: 210}),
                    getUrl: () => store.LogoID === null || this.getImage(store.LogoID),
                    imageSize: () => minSize
                },
                backdrop: 'static'
            }).result;
            if (image) {
                await this.afterUploadHandling(store, image);
            }
        } catch (error) {
            return;
        } finally {
            this.isOnLogoEdit = false;
        }
    }

    public removeCover = async (shop) => {
        try {
            await this.popupService.confirm({
                title: this.$translate.instant('remove_cover'),
                content: `<p>${this.$translate.instant('remove_cover_confirm')}</p>`,
                cancelClass: 'btn-success',
                okTxt: this.$translate.instant('no'),
                cancelTxt: this.$translate.instant('yes'),
                invertAction: true
            });
            this._removeCover(shop);
        } catch (error) { return; }
    }

    public removeLogo = async (shop) => {
        try {
            await this.popupService.confirm({
                title: this.$translate.instant('remove_logo'),
                content: `<p>${this.$translate.instant('remove_logo_confirm')}</p>`,
                cancelClass: 'btn-success',
                okTxt: this.$translate.instant('no'),
                cancelTxt: this.$translate.instant('yes'),
                invertAction: true
            });
            this._removeLogo(shop);
        } catch (error) { return; }
    }

    public restore = async (shopId) => {
        try {
            await this.popupService.confirm({
                title: this.$translate.instant('restore_place'),
                content: `<p>${this.$translate.instant('restore_place_promt')}</p>`,
                cancelClass: 'btn-success',
                okTxt: this.$translate.instant('no'),
                cancelTxt: this.$translate.instant('yes'),
                invertAction: true
            });
            this.waiting = true;
            this.notificationService.showWaitingMessage();
            await this.shopService.restoreStore(shopId);
            this.notificationService.clearAll();
            this.waiting = false;
            this.$rootScope.$state.go('app.shop', { id: shopId });

        } catch (error) {
            if (!PopupServiceCancel.isCancelOrBackdropClickInstance(error)) {
                this.notificationService.showErrorMessage(error);
            }
        }
    }

    public $onInit = async () => {
        await this.get();
        this.$scope.$watchGroup(['vm.settings.active.page', 'vm.settings.archived.page'], this.get);
    }

    public isShowInventory(type: number) {
        return this.shopService.isShowInventory(type);
    }

    private get = async () => {
        this.waiting = true;
        const storeName = this.storeName.trim();
        this.notificationService.showWaitingMessage();
        try {
            const response: any = await this.$q.all([this.shopService.getStores(this.settings.getPagingSettings(true, storeName)), this.shopService.getStores(this.settings.getPagingSettings(false, storeName)), this.$q.when(this.shopService.getBusinessTypes())]);
            this.shops = response[0].Items;
            this.archivedShops = response[1].Items;
            this.settings.active.total = response[0].Total;
            this.settings.archived.total = response[1].Total;
            this.businessTypes = response[2] as IShopBusinessType[];
            if (this.settings.active.total === 0 && this.settings.archived.total === 0) {
                this.showActiveShop = true;
            }

            this.setClientModel(this.shops, this.businessTypes);
            this.setClientModel(this.archivedShops, this.businessTypes);
            this.notificationService.clearAll();
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }

    private _removeLogo = async (store: IStoreListItem) => {
        this.waiting = true;
        this.notificationService.showWaitingMessage();

        try {
            if (await this.shopService.removeLogo(store.LogoID)) {
                this.notificationService.showSuccessMessage();
                store.logoUrl = null;
                store.LogoID = null;
            } else {
                this.notificationService.showErrorMessage();
            }
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }
    private setClientModel = (shops: IStoreListItem[], types: IShopBusinessType[]) => {
        _.forEach(shops, (shop) => {
            shop.businessType = _.findWhere(types, { value: shop.BusinessType });
            shop.coverUrl = shop.CoverID ? this.retailerService.getStoreCoverUrl(shop.CoverID, this.size.cover.thumb.w, this.size.cover.thumb.h) : null;
            shop.logoUrl = shop.LogoID ? this.retailerService.getStoreLogoUrl(shop.LogoID, this.size.logo.thumb.w, this.size.logo.thumb.h) : null;
            shop.address = `${shop.Address}, ${shop.Town}`;
        });
    }

    private _uploadedCover = async (store: IStoreListItem) => {
        if (!store || !this.file.cover) {
            return;
        } else if (this.file.cover.error) {
            this.notificationService.showErrorMessage(this.file.cover.error as any);
            this.file.cover = null;
            return;
        }
        this.notificationService.showWaitingMessage();
        this.waiting = true;

        try {
            const imageID: number = await this.shopService.uploadCover(store.ID, this.file.cover);
            this.notificationService.showSuccessMessage();
            const image = await this.blobService.getDataImageByUrl(this.retailerService.getStoreCoverUrl(imageID, this.size.cover.thumb.w, this.size.cover.thumb.h));
            store.coverUrl = image.dataUri;
            store.CoverID = imageID;
            this.file.cover = null;

        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }

    private afterUploadHandling = async (store: IStoreListItem, image: string) => {
        this.waiting = true;
        try {
            if (store.LogoID) {
                await this.shopService.removeLogo(store.LogoID);
            }
            const logoId = await this.shopService.uploadLogo(store.ID, {name: '', dataUri: image});
            const imageData = await this.blobService.getDataImageByUrl(this.retailerService.getStoreLogoUrl(logoId, this.size.logo.thumb.w, this.size.logo.thumb.h));
            store.logoUrl = imageData.dataUri;
            store.LogoID = logoId;
            this.file.logo = null;
            this.notificationService.showSuccessMessage();
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        } finally {
            this.waiting = false;
        }
    }

    private async getImage(logoId: number) {
        if (logoId) {
            try {
                const currentImage = await this.blobService.getDataImageByUrl(
                    this.retailerService.getStoreLogoUrl(logoId)
                );
                return currentImage.dataUri;
            } catch (error) {
                this.notificationService.showErrorMessage(error);
            }
        }
        return null;
    }

    private _removeCover = async (store: IStoreListItem) => {
        this.waiting = true;
        this.notificationService.showWaitingMessage();
        try {
            if (await this.shopService.removeCover(store.CoverID)) {
                this.notificationService.showSuccessMessage();
                store.coverUrl = null;
                store.CoverID = null;
            } else {
                this.notificationService.showErrorMessage();
            }
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }
}

angular.module('naut').component('shops', {
    template: require('./shops.html'),
    controller: ShopsController,
    controllerAs: 'vm'
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/places/components/shops/shops.component.ts